import * as React from 'react';

import './DestinationStep.scss';

import { ModalHeader } from '../design-system/components';

import fieldGuideLockup from '../../images/crunchafi-fieldguide.svg';

import { DestinationStep, DestinationStepLabels, DestinationStepper } from './DestinationStepper';

export type DestinationAccount = {
    name: string;
    detail?: string;
};

export type DestinationFrameProps = {
    heading: string;
    onClose: () => void;
    activeStep: DestinationStep;
    stepLabels: DestinationStepLabels;
    logoAlt: string;
    companyName?: string;
    engagementName?: string;
    requestSummary?: string;
    account: DestinationAccount | undefined;
    children: React.ReactNode;
};

/** First letters of the first and last words ("Kurt Vonnegut" gives "KV"); one letter for a single word or an email. */
function initialsOf(name: string): string {
    const words = name.trim().split(/\s+/);
    const first = words[0].charAt(0);
    const last = words.length > 1 ? words[words.length - 1].charAt(0) : '';
    return `${first}${last}`.toUpperCase();
}

/**
 * The POC wizard frame (Crunchafi.POC ModalShell with a StepSidebar): a left rail with the lockup, the step list and
 * the connected account, and a right pane with the heading and close X above the step's own content and footer.
 */
export const DestinationFrame: React.FC<DestinationFrameProps> = (props): React.ReactElement => {
    const { heading, onClose, activeStep, stepLabels, logoAlt, companyName, engagementName, requestSummary, account, children } = props;

    return (
        <div className={'field-guide-destination'}>
            <nav className={'page-step-sidebar'} aria-label={logoAlt}>
                <div className={'page-step-sidebar__title'}>
                    <img className={'page-step-sidebar__logo'} src={fieldGuideLockup} alt={logoAlt} />
                </div>
                <DestinationStepper activeStep={activeStep} labels={stepLabels} companyName={companyName} engagementName={engagementName} requestSummary={requestSummary} />
                {account && (
                    // Display only: the POC's trigger opens a Disconnect menu, which this story does not build.
                    <div className={'field-guide-account cr-menu-user-item'}>
                        <span className={'cr-avatar cr-avatar--small cr-menu-user-item__avatar'}>
                            <span className={'cr-avatar__initials'}>{initialsOf(account.name)}</span>
                        </span>
                        <span className={'cr-menu-user-item__body'}>
                            <span className={'cr-menu-user-item__name'}>{account.name}</span>
                            {account.detail && (
                                <span className={'cr-menu-user-item__details'}>
                                    <span className={'cr-menu-user-item__email'}>{account.detail}</span>
                                </span>
                            )}
                        </span>
                    </div>
                )}
            </nav>
            <div className={'field-guide-destination__main'}>
                <ModalHeader title={heading} onClose={onClose} />
                <div className={'field-guide-destination__body'}>{children}</div>
            </div>
        </div>
    );
};
